import { CustomerRequirement } from '../types/service';
import { API_BASE_URL } from './api';

// Log the API base URL for debugging
console.log('CustomerRequirements - Using API Base URL:', API_BASE_URL);

export const getCustomerRequirements = async (): Promise<CustomerRequirement[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/customer-requirements`);
    if (!response.ok) throw new Error('Failed to fetch customer requirements');

    const result = await response.json();

    // Handle the API response structure - data is wrapped in a 'data' property
    const requirements = result.data || [];

    console.log('Customer requirements fetched:', requirements);
    return requirements;
  } catch (error) {
    console.error('Error fetching customer requirements:', error);
    throw error;
  }
};

export const getCustomerRequirementById = async (id: number): Promise<CustomerRequirement> => {
  try {
    const response = await fetch(`${API_BASE_URL}/customer-requirements/${id}`);
    if (!response.ok) throw new Error(`Failed to fetch requirement with id ${id}`);

    const result = await response.json();

    // Handle the API response structure - data is wrapped in a 'data' property
    const requirement = result.data;

    if (!requirement) {
      throw new Error(`Requirement with id ${id} not found`);
    }

    console.log('Customer requirement fetched:', requirement);
    return requirement;
  } catch (error) {
    console.error(`Error fetching requirement with id ${id}:`, error);
    throw error;
  }
};

export const createCustomerRequirement = async (formData: FormData): Promise<CustomerRequirement> => {
  try {
    console.log('FormData contents:');
    for (const [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }

    const response = await fetch(`${API_BASE_URL}/customer-requirements`, {
      method: 'POST',
      // ⚠️ Don't set Content-Type manually (fetch sets it automatically for FormData)
      body: formData,
    });

    const text = await response.text();
    let result;

    try {
      result = JSON.parse(text);
    } catch {
      console.error('Invalid JSON response:', text);
      throw new Error(text);
    }

    if (!response.ok) {
      console.error('API Error Response:', result);
      throw new Error(result?.message || 'Failed to create requirement');
    }

    const requirement = result?.data || result;
    console.log('Customer requirement created:', requirement);
    return requirement;
  } catch (error) {
    console.error('Error creating requirement:', error);
    throw error;
  }
};

export const updateCustomerRequirement = async (id: number, formData: FormData): Promise<CustomerRequirement> => {
  try {
    // Log what we're sending for debugging
    console.log('Updating customer requirement with FormData:');
    for (const [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }

    const response = await fetch(`${API_BASE_URL}/customer-requirements/${id}`, {
      method: 'PUT',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      throw new Error(errorText || 'Failed to update requirement');
    }

    const result = await response.json();

    // Handle the API response structure - data is wrapped in a 'data' property
    const requirement = result.data;

    if (!requirement) {
      throw new Error('Failed to update requirement - no data returned');
    }

    console.log('Customer requirement updated:', requirement);
    return requirement;
  } catch (error) {
    console.error(`Error updating requirement with id ${id}:`, error);
    throw error;
  }
};

export const deleteCustomerRequirement = async (id: number): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/customer-requirements/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to delete requirement');
    }
  } catch (error) {
    console.error(`Error deleting requirement with id ${id}:`, error);
    throw error;
  }
};
